import { AppState, ComponentConfig, DefaultComponentProps, Fields, WithId, WithPuckProps } from '@measured/puck';
import { PageModel, PanelModel } from '@psyomics/surveyjs-core';
import { JSX, useMemo } from 'react';

import { RenderContext } from '../context/render-context';
import { getReportMetadata, PuckReportMetadata } from '../report-types';
import { Answers, Survey, SurveySet } from '../survey-types';

import * as answerUtils from './surveys/answers';
import * as fieldUtils from './surveys/fields';
import * as surveyUtils from './surveys/surveys';

type BlockProps = {
  selectedRespondentType?: string;
  selectedPageName?: string;
  selectedPanelName?: string;
  exampleAnswerJson?: string;
};

type BlockRenderProps = {
  survey: Survey;
  answers: Answers;
  page?: PageModel;
  panel?: PanelModel;
};

type Render<T extends DefaultComponentProps> = (props: WithPuckProps<T>) => JSX.Element | null;

type ResolveFields<T extends DefaultComponentProps> = (
  data: {
    props: BlockProps & Partial<BlockRenderProps> & T;
  },
  params: { fields: Fields<T>; appState: AppState }
) => Fields<T>;

interface IBlockConfig<T extends DefaultComponentProps>
  extends Omit<Omit<ComponentConfig<BlockProps>, 'render'>, 'resolveFields' | 'defaultProps'> {
  render: Render<T>;
  fields?: Fields<T>;
  resolveFields?: ResolveFields<T>;
  defaultProps?: Partial<T>;
}

const getBlockRenderProps = ({
  surveySet,
  selectedRespondentType,
  selectedPageName,
  selectedPanelName,
  exampleAnswerJson,
  puckMetadata,
}: {
  surveySet: SurveySet;
  puckMetadata?: PuckReportMetadata;
} & BlockProps): {
  survey?: Survey;
  answers?: Answers;
  page?: PageModel;
  panel?: PanelModel;
  allAnswers: Record<string, Answers | undefined>;
} => {
  const survey = surveyUtils.getSurveyByRespondentType(surveySet, selectedRespondentType);

  const answers = survey
    ? answerUtils.getAnswersForSurvey(
        survey,
        puckMetadata?.answers,
        answerUtils.parseExampleAnswerJsonField(exampleAnswerJson)
      )
    : undefined;

  const allAnswers = surveySet.reduce<Record<string, Answers | undefined>>(
    (acc, s) => ({
      ...acc,
      [s.respondentType]: answerUtils.getAnswersForSurvey(
        s,
        puckMetadata?.answers,
        answerUtils.parseExampleAnswerJsonField(exampleAnswerJson)
      ),
    }),
    {}
  );

  const page = selectedPageName && survey ? surveyUtils.getPageByName(survey, selectedPageName) : undefined;
  const panel = selectedPanelName && survey ? surveyUtils.getPanelByName(survey, selectedPanelName) : undefined;
  return { allAnswers, answers, page, panel, survey };
};

const createFields = <T extends DefaultComponentProps>({
  fields,
  renderProps,
  withExampleAnswersJson,
  props,
  surveySet,
  withPageSelect,
}: {
  fields: Fields<BlockProps & T>;
  renderProps: Partial<BlockRenderProps>;
  withExampleAnswersJson?: boolean;
  props: WithId<BlockProps & T>;
  surveySet: SurveySet;
  withPageSelect?: boolean;
}) => ({
  ...fields,
  ...(renderProps.survey
    ? {
        ...(withExampleAnswersJson && {
          exampleAnswerJson: fieldUtils.createExampleAnswerJsonField(renderProps.survey, props.selectedPageName),
        }),
        selectedRespondentType: fieldUtils.createSurveySelectField(surveySet),
        ...(withPageSelect && {
          selectedPageName: fieldUtils.createPageSelectField(renderProps.survey),
          selectedPanelName: fieldUtils.createPanelSelectField(renderProps.survey, props.selectedPageName),
        }),
      }
    : {
        selectedRespondentType: fieldUtils.createSurveySelectField(surveySet),
      }),
});

const createResolveFields =
  <T extends DefaultComponentProps>({
    componentConfig,
    withPageSelect,
    withExampleAnswersJson,
    surveySet,
  }: {
    componentConfig: IBlockConfig<T>;
    withPageSelect?: boolean;
    withExampleAnswersJson?: boolean;
    surveySet: SurveySet;
  }) =>
  (data: { props: WithId<BlockProps & T> }, params: { fields: Fields<BlockProps & T>; appState: AppState }) => {
    const renderProps = getBlockRenderProps({
      exampleAnswerJson: data.props.exampleAnswerJson,
      selectedPageName: data.props.selectedPageName,
      selectedPanelName: data.props.selectedPanelName,
      selectedRespondentType: data.props.selectedRespondentType,
      surveySet,
    });

    const resolvedFields = createFields({
      fields: params.fields,
      props: data.props,
      renderProps,
      surveySet,
      withExampleAnswersJson,
      withPageSelect,
    });

    if (!componentConfig.resolveFields) return resolvedFields;

    return componentConfig.resolveFields(
      {
        ...data,
        props: {
          ...data.props,
          ...renderProps,
        },
      },
      {
        fields: {
          ...params.fields,
          ...resolvedFields,
        },
        appState: params.appState,
      }
    );
  };

const createBlockRender =
  <T extends DefaultComponentProps>({
    componentConfig,
    surveySet,
    withPageSelect,
    overrides,
  }: {
    componentConfig: IBlockConfig<T>;
    surveySet: SurveySet;
    withPageSelect?: boolean;
    overrides?: Partial<BlockProps>;
  }) =>
  (props: WithPuckProps<BlockProps & T>) => {
    const getRenderData = () =>
      getBlockRenderProps({
        exampleAnswerJson: props.exampleAnswerJson,
        puckMetadata: getReportMetadata(props.puck.metadata),
        selectedPageName: props.selectedPageName,
        selectedPanelName: props.selectedPanelName,
        selectedRespondentType: props.selectedRespondentType,
        surveySet,
        ...overrides,
      });

    const { survey, answers, page, panel, allAnswers } = useMemo(getRenderData, [
      props.exampleAnswerJson,
      props.puck.metadata,
      props.selectedPageName,
      props.selectedPanelName,
      props.selectedRespondentType,
    ]);

    const contextValue = useMemo(
      () => ({
        allAnswers,
        answers,
        page,
        panel,
        selectedRespondentType: props.selectedRespondentType,
        survey,
        surveySet,
      }),
      [allAnswers, answers, page, panel, props.selectedRespondentType, survey]
    );

    if (!survey) return <div />;
    if (!page && withPageSelect) return <div>No page selected.</div>;
    const Renderer = componentConfig.render;

    return (
      <RenderContext.Provider value={contextValue}>
        <Renderer {...props} />
      </RenderContext.Provider>
    );
  };

const makeBlockFactory =
  <T extends DefaultComponentProps>(
    {
      withPageSelect,
      withExampleAnswersJson,
      overrides,
    }: {
      withPageSelect?: boolean;
      withExampleAnswersJson?: boolean;
      overrides?: Partial<BlockProps>;
    },
    componentConfig: IBlockConfig<T>
  ) =>
  ({ surveySet }: { surveySet: SurveySet }) => {
    const resolveFields = createResolveFields<T>({
      surveySet,
      withExampleAnswersJson,
      withPageSelect,
      componentConfig,
    });

    const render = createBlockRender({
      componentConfig,
      surveySet,
      withPageSelect,
      overrides,
    });

    return {
      ...componentConfig,
      render,
      resolveFields,
    };
  };

export { makeBlockFactory, type ResolveFields };
